import type { GetPartnersArgs, Partner } from '@types'
import { PartnerCategories, PartnerRegions } from 'constants/enums'

const PARTNERS_URL = '/partners/partners.data.json'
const DEFAULT_LIMIT = 10

type PartnerServiceReturn = {
  partners: Partner[]
  total: number
  count: number
}

export class PartnerService {
  private partners: Partner[] = []

  constructor() {}

  private async getPartnersData() {
    const response = await fetch(PARTNERS_URL)
    const responseJson = await response.json()
    return responseJson.partners.sort((a: Partner, b: Partner) =>
      a.name?.localeCompare(b.name ?? '')
    )
  }

  private search(partners: Partner[], search: string): Partner[] {
    const searchTerm = search.toLowerCase()

    return partners.filter((partner) => {
      return (
        partner.name?.toLowerCase().includes(searchTerm) ||
        partner.description?.toLowerCase().includes(searchTerm)
      )
    })
  }

  private paginate(
    partners: Partner[],
    { offset = 0, limit = DEFAULT_LIMIT }: GetPartnersArgs
  ) {
    return partners.slice(offset, offset + limit)
  }

  private filterByRegions(partners: Partner[], region: PartnerRegions) {
    return partners.filter(({ regions }) => regions?.includes(region))
  }

  private filterByCategories(
    partners: Partner[],
    categories: PartnerCategories[]
  ) {
    return partners.filter(({ categories: partnerCategories }) =>
      categories.some((category) => partnerCategories?.includes(category))
    )
  }

  private filterPartners({ region, categories, search }: GetPartnersArgs) {
    let filteredPartners = this.partners

    if (search && search.trim()) {
      return this.search(filteredPartners, search.trim())
    }

    if (region) {
      filteredPartners = this.filterByRegions(filteredPartners, region)
    }

    if (categories?.length) {
      filteredPartners = this.filterByCategories(filteredPartners, categories)
    }

    return filteredPartners
  }

  async getPartners(filters?: GetPartnersArgs): Promise<PartnerServiceReturn> {
    if (this.partners.length === 0) {
      this.partners = await this.getPartnersData()
    }

    const partners = this.filterPartners(filters ?? {})
    const paginated = this.paginate(partners, filters ?? {})

    return {
      partners: paginated,
      count: paginated.length,
      total: partners.length,
    }
  }
}
